import axios from 'axios';
import { BlockBlobClient } from '@azure/storage-blob';
import { loadingStore } from './loadingStore';
import { ADMIN_ROLE, type AdminRole, ADMIN_STATUS, type AdminStatus } from './constants/admin';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

export const api = axios.create({
  baseURL,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' }
});

// Public API instance without 401 auto-logout (useful for login endpoints to surface errors)
export const publicApi = axios.create({
  baseURL,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' }
});

export function logout() {
  try {
    localStorage.removeItem('admin-auth-token');
    localStorage.removeItem('admin-auth-expiry');
  } catch {}
  if (typeof window !== 'undefined') {
    window.location.href = '/admin/login';
  }
}

// Attach Authorization header if token exists
api.interceptors.request.use((config) => {
  // increment global loading count
  loadingStore.increment();
  try {
    const token = localStorage.getItem('admin-auth-token');
    if (token) {
      config.headers = config.headers || {};
      (config.headers as any)['Authorization'] = `Bearer ${token}`;
    }
  } catch {}
  return config;
});

// Handle 401 to force login
api.interceptors.response.use(
  (res) => {
    // decrement on success
    loadingStore.decrement();
    return res;
  },
  (err) => {
    // decrement on error
    loadingStore.decrement();
    if (err?.response?.status === 401) {
      logout();
    }
    return Promise.reject(err);
  }
);

// publicApi should also affect loading (e.g., login)
publicApi.interceptors.request.use((config) => {
  loadingStore.increment();
  return config;
});

publicApi.interceptors.response.use(
  (res) => {
    loadingStore.decrement();
    return res;
  },
  (err) => {
    loadingStore.decrement();
    return Promise.reject(err);
  }
);

export async function uploadSingleFile(file: File) {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post(`${baseURL}/api/files/upload`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.file as { url: string; blobName: string; originalName: string };
}

// Get a write-enabled SAS URL for a new blob based on the original filename
export async function getUploadSas(originalName: string, prefix?: string): Promise<{ sasUrl: string; blobUrl: string; blobName: string }> {
  const res = await api.post(`/api/files/upload-sas`, { originalName, prefix });
  return res.data as { sasUrl: string; blobUrl: string; blobName: string };
}

// Upload a file directly to Azure Blob Storage using SAS URL, optimized for large files
// Allows optional onProgress callback and AbortSignal for cancel support
export async function uploadDirectToBlob(
  file: File,
  prefix?: string,
  options?: {
    onProgress?: (ev: { loadedBytes: number }) => void;
    abortSignal?: AbortSignal;
    blockSizeMB?: number; // default 4MB
    concurrency?: number; // default 4
  }
): Promise<{ url: string; blobName: string }> {
  // Manually toggle loading since this path bypasses axios
  loadingStore.increment();
  try {
    const { sasUrl, blobUrl, blobName } = await getUploadSas(file.name, prefix);

    const contentType = file.type || 'application/octet-stream';
    const client = new BlockBlobClient(sasUrl);

    const blockSize = Math.max(1, Math.floor((options?.blockSizeMB ?? 4))) * 1024 * 1024; // MB to bytes
    const concurrency = Math.max(1, options?.concurrency ?? 4);

    await client.uploadData(file, {
      blockSize,
      concurrency,
      abortSignal: options?.abortSignal,
      onProgress: options?.onProgress,
      blobHTTPHeaders: { blobContentType: contentType },
    });

    return { url: blobUrl, blobName };
  } finally {
    loadingStore.decrement();
  }
}

// ===== Admin Account APIs =====
export type AdminUser = { id: number; username: string; email?: string | null; role: AdminRole | undefined; status?: AdminStatus };

export async function getCurrentAdmin(): Promise<AdminUser> {
  const res = await api.get('/api/admin/auth/me');
  return res.data as AdminUser;
}

export async function updateAccountUsername(payload: { newUsername: string; password: string }): Promise<AdminUser> {
  const res = await api.put('/api/admin/auth/account/username', payload);
  return res.data as AdminUser;
}

export async function changeAccountPassword(payload: { oldPassword: string; newPassword: string }): Promise<{ success: boolean }> {
  const res = await api.put('/api/admin/auth/account/password', payload);
  return res.data as { success: boolean };
}

// ===== Password Reset APIs =====
export async function requestPasswordReset(email: string): Promise<{ success: boolean }> {
  const res = await api.post('/api/admin/auth/password/reset/request', { email });
  return res.data as { success: boolean };
}

export async function confirmPasswordReset(token: string, newPassword: string): Promise<{ success: boolean }> {
  const res = await api.post('/api/admin/auth/password/reset/confirm', { token, newPassword });
  return res.data as { success: boolean };
}

export async function validatePasswordResetToken(token: string): Promise<{ valid: boolean; error?: string }> {
  const res = await api.get('/api/admin/auth/password/reset/validate', { params: { token } });
  return res.data as { valid: boolean; error?: string };
}

// ===== Super Admin: Admin Users Management =====
export async function listAdminUsers(): Promise<AdminUser[]> {
  const res = await api.get('/api/admin/users');
  return res.data as AdminUser[];
}

export async function createSubAdmin(payload: { email: string; password: string; username?: string }): Promise<AdminUser> {
  const res = await api.post('/api/admin/users', payload);
  return res.data as AdminUser;
}

export async function deleteAdminUser(id: number): Promise<void> {
  await api.delete(`/api/admin/users/${id}`);
}

// Update admin user. If username (email) changes, backend sends verification email and marks status as 未確認 until verified.
export async function updateAdminUser(id: number, payload: { password?: string; username?: string }): Promise<AdminUser> {
  const res = await api.put(`/api/admin/users/${id}`, payload);
  return res.data as AdminUser;
}

// Confirm email change by token (public endpoint). Returns { success: boolean }
export async function confirmAdminEmailChange(token: string): Promise<{ success: boolean; error?: string }> {
  const res = await publicApi.get('/api/admin/users/verify-email', { params: { token } });
  return res.data as { success: boolean; error?: string };
}

// Resend verification email for an admin user (SUPER only)
export async function resendAdminVerification(id: number): Promise<{ success: boolean }> {
  const res = await api.post(`/api/admin/users/${id}/resend-verification`);
  return res.data as { success: boolean };
}
